import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows after the main buy buttons have been scrolled past (above the
 * viewport), and hides while they are still in view or still below the fold.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {MutationObserver | null} */
  #preorderLabelObserver = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #hasPlayedSizeScrollHint = false;

  /** @type {Element | null} */
  #buyButtonsBlock = null;

  /** @type {number | null} */
  #visibilityRaf = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    window.addEventListener('scroll', this.#handleScroll, { passive: true, signal });

    this.#getInitialQuantity();

    if (this.dataset.stickyStyle === 'solid_compact') {
      this.#syncChatFabOffset(0);
    }

    this.#syncStickyVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#preorderLabelObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
    if (this.#visibilityRaf != null) {
      cancelAnimationFrame(this.#visibilityRaf);
      this.#visibilityRaf = null;
    }
    this.#syncChatFabOffset(0);
  }

  /**
   * Sets up observers for buy-button and footer visibility.
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#buyButtonsBlock = buyButtonsBlock;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver(() => {
      this.#syncStickyVisibility();
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          this.#hiddenByBottom = false;
          this.#syncStickyVisibility();
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    this.#watchPreorderLabel();
  }

  #handleScroll = () => {
    if (this.#visibilityRaf != null) return;
    this.#visibilityRaf = requestAnimationFrame(() => {
      this.#visibilityRaf = null;
      this.#syncStickyVisibility();
    });
  };

  /**
   * Shows the sticky bar only after the original add-to-cart has been
   * scrolled past (fully above the viewport). Stays hidden on landing
   * while that button is still below the fold, and while it is on screen.
   */
  #syncStickyVisibility() {
    if (this.#isChatActive()) {
      if (this.#isStuck) this.#hideStickyBar();
      return;
    }

    const buyButtonsBlock = this.#buyButtonsBlock;
    if (!buyButtonsBlock) return;

    const rect = buyButtonsBlock.getBoundingClientRect();
    const inView = rect.top < window.innerHeight && rect.bottom > 0;
    const scrolledPast = this.#isAboveViewport(rect);

    if (inView) {
      this.#hiddenByBottom = false;
      if (this.#isStuck) this.#hideStickyBar();
      return;
    }

    if (scrolledPast && !this.#hiddenByBottom) {
      if (!this.#isStuck) this.#showStickyBar();
    } else if (this.#isStuck) {
      this.#hideStickyBar();
    }
  }

  // Public action handlers
  /**
   * Syncs size selection from the sticky bar into the main variant picker.
   * @param {MouseEvent} event
   */
  handleStickySizeClick = (event) => {
    // Component click delegation proxies `event.target` to the `on:click` element.
    // `currentTarget` is the document listener, so it must not be used here.
    const button = /** @type {HTMLElement | null} */ (
      event.target instanceof HTMLElement ? event.target.closest('[data-sticky-size]') : null
    );
    if (!button || button.hasAttribute('disabled')) return;

    const optionValueId = button.dataset.optionValueId;
    const value = button.dataset.stickySize;
    const optionName = button.dataset.optionName;
    if ((!optionValueId && !value) || !optionName) return;

    const productId = this.dataset.productId;
    const picker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!picker) return;

    /** @type {HTMLInputElement | undefined} */
    let match;
    if (optionValueId) {
      const byId = picker.querySelector(`input[data-option-value-id="${CSS.escape(optionValueId)}"]`);
      if (byId instanceof HTMLInputElement && !byId.disabled) match = byId;
    }
    if (!match && value) {
      const inputs = picker.querySelectorAll(`input[data-option-name="${CSS.escape(optionName)}"]`);
      match = Array.from(inputs).find(
        (input) => input instanceof HTMLInputElement && input.value === value && !input.disabled
      );
    }
    if (!(match instanceof HTMLInputElement) || match.checked) return;

    // Optimistic UI while section morph catches up
    const group = button.closest('.sticky-add-to-cart__sizes');
    group?.querySelectorAll('.sticky-add-to-cart__size').forEach((chip) => {
      const selected = chip === button;
      chip.classList.toggle('sticky-add-to-cart__size--selected', selected);
      chip.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    match.click();
  };

  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId, variantId } = event.detail ?? {};
    if (variantId) {
      this.dataset.currentVariantId = variantId;
    } else if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Mirror ATC sold-out / available state immediately (don't wait on section fetch)
    this.#applyOptimisticAvailability(event);

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
          this.#watchPreorderLabel();
        }

        this.#syncStickyOffset();
        this.#scrollSelectedSizeIntoView();
        this.#bindSizeScrollerFade();

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * @param {ProductSelectEvent} event
   */
  #applyOptimisticAvailability(event) {
    const { optionValueId } = event.detail ?? {};
    const productId = this.dataset.productId;
    if (!productId) return;

    const picker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!picker) return;

    /** @type {HTMLInputElement | null} */
    let changedOption = null;
    if (optionValueId) {
      const match = picker.querySelector(`input[data-option-value-id="${CSS.escape(optionValueId)}"]`);
      if (match instanceof HTMLInputElement) changedOption = match;
    }

    /** @type {boolean | null} */
    let available = null;
    if (changedOption?.dataset.optionAvailable === 'false') {
      available = false;
    } else {
      const checked = /** @type {HTMLInputElement[]} */ ([...picker.querySelectorAll('input:checked')]);
      if (checked.length > 0 && checked.every((input) => input.dataset.optionAvailable !== 'false')) {
        available = true;
      }
    }
    if (available == null) return;

    this.dataset.variantAvailable = available ? 'true' : 'false';
    const { addToCartButton } = this.refs;
    if (!addToCartButton) return;

    addToCartButton.disabled = !available;
    const labelEl = addToCartButton.querySelector('.add-to-cart-text__content > span:first-child');
    if (labelEl) {
      labelEl.textContent = available
        ? this.dataset.textAddToCart || 'Add to cart'
        : this.dataset.textSoldOut || 'Sold out';
    }
    addToCartButton.setAttribute(
      'aria-label',
      available ? this.dataset.textAddToCart || 'Add to cart' : this.dataset.textSoldOut || 'Sold out'
    );
    this.#syncPreorderLabel();
  }

  /**
   * Restock Rocket rewrites the main submit button. Mirror that label onto the sticky bar.
   */
  #watchPreorderLabel() {
    this.#preorderLabelObserver?.disconnect();
    const source = this.#targetAddToCartButton;
    if (!source) return;

    this.#preorderLabelObserver = new MutationObserver(() => {
      this.#syncPreorderLabel();
    });
    this.#preorderLabelObserver.observe(source, {
      attributes: true,
      attributeFilter: ['class', 'disabled'],
      childList: true,
      subtree: true,
      characterData: true,
    });
    this.#syncPreorderLabel();
  }

  /**
   * @returns {boolean} Whether a pre-order label was applied
   */
  #syncPreorderLabel() {
    const source = this.#targetAddToCartButton;
    const { addToCartButton } = this.refs;
    if (!source || !addToCartButton || !source.classList.contains('restock-rocket-preorder-button')) return false;

    const text = (source.querySelector('.add-to-cart-text')?.textContent || '').replace(/\s+/g, ' ').trim();
    if (!text) return false;

    const labelEl = addToCartButton.querySelector('.add-to-cart-text__content > span:first-child');
    if (labelEl) labelEl.textContent = text;
    addToCartButton.setAttribute('aria-label', text);
    addToCartButton.disabled = source.disabled;
    return true;
  }

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    this.#syncStickyOffset();
    this.#playSizeScrollHint();
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    this.#syncStickyOffset();
  }

  /**
   * One-shot horizontal nudge so shoppers notice the size chips can scroll.
   * Delayed so it doesn't read as a layout glitch on appear.
   * Skips if content fits, reduced-motion is on, or the hint already played.
   */
  #playSizeScrollHint() {
    if (this.dataset.stickyStyle !== 'solid_compact') return;

    this.#bindSizeScrollerFade();
    this.#scrollSelectedSizeIntoView({ smooth: false });

    if (this.#hasPlayedSizeScrollHint) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const scroller = this.querySelector('.sticky-add-to-cart__sizes-scroller');
    if (!(scroller instanceof HTMLElement)) return;
    if (scroller.scrollWidth <= scroller.clientWidth + 8) return;

    this.#hasPlayedSizeScrollHint = true;
    const startLeft = scroller.scrollLeft;
    const distance = Math.min(48, Math.floor((scroller.scrollWidth - scroller.clientWidth) * 0.35));

    window.setTimeout(() => {
      if (!this.#isStuck) return;
      if (!(scroller.isConnected)) return;

      scroller.scrollTo({ left: startLeft + distance, behavior: 'smooth' });

      window.setTimeout(() => {
        if (!this.#isStuck || !scroller.isConnected) return;
        scroller.scrollTo({ left: startLeft, behavior: 'smooth' });
      }, 520);
    }, 1100);
  }

  /**
   * Keeps the selected size chip visible in the horizontal scroller.
   * @param {{ smooth?: boolean }} [options]
   */
  #scrollSelectedSizeIntoView({ smooth = true } = {}) {
    const selected = this.querySelector(
      '.sticky-add-to-cart__size--selected, .sticky-add-to-cart__size[aria-pressed="true"]'
    );
    if (!(selected instanceof HTMLElement)) return;

    const scroller = selected.closest('.sticky-add-to-cart__sizes-scroller');
    if (!(scroller instanceof HTMLElement)) return;

    const pad = 8;
    const selectedLeft = selected.offsetLeft;
    const selectedRight = selectedLeft + selected.offsetWidth;
    const viewLeft = scroller.scrollLeft;
    const viewRight = viewLeft + scroller.clientWidth;
    /** @type {ScrollBehavior} */
    const behavior = smooth ? 'smooth' : 'auto';

    if (selectedLeft < viewLeft + pad) {
      scroller.scrollTo({ left: Math.max(0, selectedLeft - pad), behavior });
    } else if (selectedRight > viewRight - pad) {
      scroller.scrollTo({ left: selectedRight - scroller.clientWidth + pad, behavior });
    }
  }

  /**
   * Adds a right-edge fade while there is more size content to scroll.
   */
  #bindSizeScrollerFade() {
    const scroller = this.querySelector('.sticky-add-to-cart__sizes-scroller');
    if (!(scroller instanceof HTMLElement)) return;

    const update = () => {
      const overflow = scroller.scrollWidth > scroller.clientWidth + 8;
      scroller.classList.toggle('sticky-add-to-cart__sizes-scroller--scrollable', overflow);
      if (!overflow) {
        scroller.removeAttribute('data-at-end');
        return;
      }
      const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 2;
      scroller.dataset.atEnd = atEnd ? 'true' : 'false';
    };

    if (!scroller.dataset.fadeBound) {
      scroller.dataset.fadeBound = 'true';
      scroller.addEventListener('scroll', update, { passive: true });
    }

    update();
  }

  #syncStickyOffset() {
    if (this.dataset.stickyStyle !== 'solid_compact') return;
    const height = this.#isStuck ? Math.ceil(this.refs.stickyBar.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--pdp-sticky-bar-offset', `${height}px`);
    this.#syncChatFabOffset(height);
  }

  /**
   * Lifts the Shopify Inbox FAB above the solid sticky bar.
   * The activator lives in shadow DOM, so we inject a small style sheet
   * that reads an inherited CSS variable for the lift amount.
   * @param {number} height
   */
  #syncChatFabOffset(height) {
    const gap = 12;
    const lift = height > 0 ? height + gap : 0;
    document.documentElement.style.setProperty('--pdp-sticky-chat-lift', `${lift}px`);

    const apply = () => {
      const chat = document.querySelector('shopify-chat');
      if (!chat?.shadowRoot) return false;

      const styleId = 'pdp-sticky-chat-offset';
      let styleEl = chat.shadowRoot.getElementById(styleId);
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = styleId;
        styleEl.textContent = `
          .activator-nudge {
            transform: translate3d(0, calc(-1 * var(--pdp-sticky-chat-lift, 0px)), 0) !important;
            transition: transform 0.25s ease;
          }
        `;
        chat.shadowRoot.appendChild(styleEl);
      }
      return true;
    };

    if (apply()) return;

    customElements.whenDefined('shopify-chat').then(() => {
      requestAnimationFrame(() => {
        if (!apply()) {
          // Shadow may mount a beat after the custom element defines
          setTimeout(apply, 100);
        }
      });
    });
  }

  // Helper methods
  /**
   * Whether an element's bounding box is fully above the viewport.
   * Used so the sticky bar appears only after scrolling past the original ATC,
   * not while that button is still waiting below the fold.
   *
   * @param {DOMRectReadOnly | DOMRect} rect
   * @returns {boolean}
   */
  #isAboveViewport(rect) {
    return rect.bottom <= 0;
  }

  /**
   * Checks whether the Shopify Chat conversation panel is open.
   * Inbox always mounts <shopify-chat> on the page, so presence alone must
   * not suppress the sticky bar — only an open chat panel should.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    const chat = document.querySelector('shopify-chat');
    if (!chat || !customElements.get('shopify-chat')) return false;

    if (chat.hasAttribute('open') || chat.getAttribute('aria-expanded') === 'true') {
      return true;
    }

    try {
      const root = chat.shadowRoot;
      if (!root) return false;
      return Boolean(root.querySelector('dialog[open], [aria-modal="true"]:not([hidden])'));
    } catch {
      return false;
    }
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
